import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Search, ChevronLeft, Edit } from "lucide-react";
import api from "../../lib/api";

const ViewReportPage = () => {
  const { id } = useParams();

  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);

  // Fetch report data
  const { data: reportResponse, isLoading } = useQuery(["report", id], async () => {
    const response = await api.get(`/reports/${id}`);
    return response.data;
  });

  const report = reportResponse?.data || reportResponse;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch clients
        const clientsRes = await api.get("/clients/all-active-clients");
        let clientList = [];
        if (clientsRes.data?.data && Array.isArray(clientsRes.data.data)) {
          clientList = clientsRes.data.data;
        } else if (Array.isArray(clientsRes.data)) {
          clientList = clientsRes.data;
        }
        setClients(clientList);

        // Fetch sites
        const sitesRes = await api.get("/sites");
        if (sitesRes.data?.data && Array.isArray(sitesRes.data.data)) {
          setSites(sitesRes.data.data);
        }

        // Fetch users
        const usersRes = await api.get("/users");
        if (usersRes.data?.data && Array.isArray(usersRes.data.data)) {
          setUsers(usersRes.data.data);
        }
      } catch (err) {
        console.error("Failed to load data:", err);
      }
    };

    fetchData();
  }, []);

  const getClientName = (clientId) => {
    if (report?.client_name) return report.client_name;

    const client = clients.find((c) => c.id === clientId || c._id === clientId);
    return client
      ? client.first_name && client.last_name
        ? `${client.first_name} ${client.last_name}`
        : client.name || client.email
      : "N/A";
  };

  const getSiteName = (siteId) => {
    if (report?.site_name) return report.site_name;

    const site = sites.find((s) => s.id === siteId);
    return site ? site.name : "N/A";
  };

  const getUserName = (userId) => {
    if (!userId) return "Not assigned";

    const user = users.find((u) => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : "N/A";
  };

  const formatStage = (stage) => {
    const stageLabels = {
      unallocated: "UNALLOCATED",
      field_engineer: "FIELD ENGINEER",
      peer_reviewer: "PEER REVIEWER",
      site_reviewer: "SITE REVIEWER",
      director_reviewer: "DIRECTOR REVIEWER",
      completed: "COMPLETED",
    };
    return stageLabels[stage] || stage?.toUpperCase() || "N/A";
  };

  const formatStatus = (status) => {
    const statusLabels = {
      awaiting: "AWAITING",
      awaiting_field_engineer: "AWAITING FIELD ENGINEER",
      started_field_engineer: "STARTED FIELD ENGINEER",
      uploaded_field_engineer: "UPLOADED FIELD ENGINEER",
      failed_peer_review: "FAILED PEER REVIEW",
      failed_site_review: "FAILED SITE REVIEW",
      failed_director_review: "FAILED DIRECTOR REVIEW",
      awaiting_peer_reviewer: "AWAITING PEER REVIEWER",
      started_peer_review: "STARTED PEER REVIEW",
      uploaded_peer_review: "UPLOADED PEER REVIEW",
      awaiting_site_reviewer: "AWAITING SITE REVIEWER",
      started_site_review: "STARTED SITE REVIEW",
      uploaded_site_review: "UPLOADED SITE REVIEW",
      awaiting_director_reviewer: "AWAITING DIRECTOR REVIEWER",
      started_director_review: "STARTED DIRECTOR REVIEW",
      uploaded_director_review: "UPLOADED DIRECTOR REVIEW",
      completed: "COMPLETED",
    };
    return statusLabels[status] || status?.toUpperCase() || "N/A";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="w-full">
        {/* Main Content */}
        <div className="slide-up">
          <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">Client:</Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {getClientName(report?.client_id)}
              </div>
            </div>
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">Site:</Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {getSiteName(report?.site_id)}
              </div>
            </div>
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">Report Name:</Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {report?.name || "N/A"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                Field Engineer:
              </Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {getUserName(report?.field_engineer_id)}
              </div>
            </div>
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                Peer Reviewer:
              </Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {getUserName(report?.peer_reviewer_id)}
              </div>
            </div>
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                Site Reviewer:
              </Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {getUserName(report?.site_reviewer_id)}
              </div>
            </div>
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                Director Reviewer:
              </Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {getUserName(report?.director_reviewer_id)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">Visit Date:</Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {report?.visited_date ? new Date(report.visited_date).toLocaleDateString() : "N/A"}
              </div>
            </div>
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                Report Stage:
              </Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {formatStage(report?.stage)}
              </div>
            </div>
            <div className="space-y-3">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                Report Status:
              </Label>
              <div className="w-full h-auto px-4 py-3 text-sm rounded-t-lg bg-navy-100 text-gray-0">
                {formatStatus(report?.status)}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end border-t border-b border-gray-0">
            <div className="flex items-center justify-center w-auto">
              <Link className="border-r rounded-b-none border-gray-0" to="/reports">
                <Button
                  variant="secondary"
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase rounded-none text-gray-0 hover:scale-100"
                >
                  <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                  Back
                </Button>
              </Link>
              <Link to={`/reports/edit/${id}`}>
                <Button className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal text-red-500 uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewReportPage;
